"use client";

import * as React from "react";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Activity, Compass, Cpu, Layers, Orbit, RefreshCw, Sparkles, Zap, CheckCircle2, ChevronRight, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Reveal } from "@/components/cosmic/Reveal";
import { SectionHeading } from "@/components/cosmic/SectionHeading";
import "@/components/cosmic/cosmic.css";
interface OutcomeData {
  title: string;
  verdict: string;
  stability: string;
  entropy: string;
  stellarLifespan: string;
  description: string;
}
const OUTCOMES: Record<string, OutcomeData> = {
  "hyper-3d": {
    title: "Projected Cosmic State: Hyper-Gravity & 3D Space",
    verdict: "Extreme Gravitational Compression",
    stability: "Critical Instability",
    entropy: "Accelerated",
    stellarLifespan: "< 50 Million Years",
    description: "Stars exhaust their hydrogen fuel in mere millions of years instead of billions, burning through stellar nucleosynthesis in violent supernovas. Planetary orbits compress into tight, unstable spirals, while biological structures larger than single cells are crushed under immense surface gravitational shear. Complex multicellular life cannot evolve before host stars collapse into black holes."
  },
  "standard-3d": {
    title: "Projected Cosmic State: Standard Reality (Our Universe)",
    verdict: "Balanced Cosmological Equilibrium",
    stability: "Stable Goldilocks Zone",
    entropy: "Gradual Decay",
    stellarLifespan: "~10 Billion Years",
    description: "Stellar nucleosynthesis proceeds steadily, allowing stars like our Sun to burn stably across billions of years. Planetary orbits settle into enduring circular paths, and chemical complexity flourishes across stable rocky planetary bodies without crushing shearing forces."
  },
  "micro-3d": {
    title: "Projected Cosmic State: Micro-Gravity Dissolution",
    verdict: "Sub-critical Stellar Collapse",
    stability: "Diffuse Cloud Dispersion",
    entropy: "Arrested Formation",
    stellarLifespan: "Non-initiating",
    description: "Molecular gas clouds lack sufficient gravitational attractive pull to overcome thermal pressure. Stellar ignition fails to initiate across the cosmos, leaving an ethereal soup of primordial cold hydrogen without coalescing planets or biological formation."
  },
  "flatland": {
    title: "Projected Cosmic State: 2D Planar Topology",
    verdict: "Planar Geometric Constraint",
    stability: "Dimensionally Constrained",
    entropy: "Uniform Planar Dissipation",
    stellarLifespan: "~2 Billion Years",
    description: "General relativity in two spatial dimensions yields no propagating gravitational waves or dynamic closed planetary orbits. Biological circulatory systems and neural networks cannot intersect without bifurcating physical bodies, severing complex biological continuity."
  },
  "hyperspace": {
    title: "Projected Cosmic State: 4D Hyper-Spacetime Dissipation",
    verdict: "Inverse-Cube Orbital Decay",
    stability: "Total Orbital Decay",
    entropy: "Instantaneous Dispersion",
    stellarLifespan: "< 10,000 Years",
    description: "In four spatial dimensions, the gravitational inverse-square law escalates to an inverse-cube decay. Stable planetary orbits become mathematically impossible: planets either hurtle into parent stars or spiral out into deep cold cosmic voids within single orbital cycles."
  },
  "sluggish": {
    title: "Projected Cosmic State: Relativistic Sluggish Photon Field",
    verdict: "Severe Relativistic Distortions",
    stability: "Extreme Doppler Shifts",
    entropy: "Electromagnetic Retardation",
    stellarLifespan: "~500 Million Years",
    description: "With light traveling at bicycle speed (100 km/h), standard locomotion induces blinding relativistic aberration and extreme Doppler shifts. Atomic electromagnetic bonding suffers severe lag, destabilizing molecular bonds and severely stifling electronic signal propagation."
  },
  "infinite": {
    title: "Projected Cosmic State: Instantaneous Velocity Field",
    verdict: "Immediate Non-local Causality",
    stability: "Causality Collapse",
    entropy: "Instantaneous Equalization",
    stellarLifespan: "Indefinite Uniformity",
    description: "An infinite speed of light dissolves special relativity entirely. Mass-energy equivalence diverges, the cosmological event horizon dissolves into instantaneous optical awareness, and thermal radiation reaches every cosmic coordinate without temporal latency."
  }
};
export default function UniverseQuiz() {
  const [activeStep, setActiveStep] = useState<number>(1);
  const [gravity, setGravity] = useState<string>("hyper");
  const [dimensions, setDimensions] = useState<string>("3d");
  const [speedOfLight, setSpeedOfLight] = useState<string>("standard");
  const getOutcomeKey = (): string => {
    if (dimensions === "2d") return "flatland";
    if (dimensions === "4d") return "hyperspace";
    if (speedOfLight === "sluggish") return "sluggish";
    if (speedOfLight === "infinite") return "infinite";
    if (gravity === "hyper") return "hyper-3d";
    if (gravity === "micro") return "micro-3d";
    return "standard-3d";
  };
  const outcome = OUTCOMES[getOutcomeKey()] || OUTCOMES["hyper-3d"];
  const handleReset = () => {
    setGravity("hyper");
    setDimensions("3d");
    setSpeedOfLight("standard");
    setActiveStep(1);
  };
  const progressPercent = activeStep / 3 * 100;
  return <section id="universe-quiz" data-nav="dark" className="relative w-full max-w-full overflow-hidden py-14 sm:py-24 border-t border-border/40">
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 min-w-0">
        {/* Section Heading with SectionHeading Component */}
        <SectionHeading eyebrow="Interactive Diagnostic Simulator" title="What-If Universe Matrix" description="Alter fundamental cosmological constants to observe how physical laws, planetary orbits, and biological stability reorganize across alternate cosmic models." align="center" className="break-words" />

        {/* Portal-Duo Layout: Columns 1-6 Controller, Columns 7-12 Terminal */}
        <Reveal delay={150}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start min-w-0">
            {/* Columns 1-6: Multi-step parameter selector */}
            <div className="lg:col-span-6 flex flex-col gap-4 sm:gap-5 min-w-0">
              {/* Animated Progress Bar */}
              <div className="ql-glass p-3 sm:p-3.5 rounded-xl border border-border flex flex-col gap-2 min-w-0">
                <div className="flex items-center justify-between text-[11px] sm:text-xs font-mono min-w-0 gap-2">
                  <span className="text-muted-foreground truncate">SIMULATION PROGRESS</span>
                  <span className="text-primary font-bold shrink-0">{Math.round(progressPercent)}%</span>
                </div>
                <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden relative">
                  <div className="h-full bg-gradient-to-r from-primary to-cyan-300 transition-all duration-300 shadow-[0_0_8px_var(--glow-primary)]" style={{
                  width: `${progressPercent}%`
                }} />
                </div>
              </div>

              {/* Step Selection Tabs */}
              <div className="grid grid-cols-3 gap-1.5 sm:gap-2 p-1.5 rounded-xl ql-glass border border-border min-w-0">
                <button type="button" onClick={() => setActiveStep(1)} className={`w-full py-2.5 px-1.5 sm:px-3 rounded-lg text-[11px] sm:text-xs font-mono font-medium transition-all cursor-pointer min-h-[44px] flex items-center justify-center text-center whitespace-normal break-words leading-tight ${activeStep === 1 ? "bg-primary text-primary-foreground font-semibold shadow-[0_0_12px_var(--glow-primary)]" : "text-foreground/80 hover:bg-card hover:text-primary"}`}>
                  <span>1. Gravity</span>
                </button>
                <button type="button" onClick={() => setActiveStep(2)} className={`w-full py-2.5 px-1.5 sm:px-3 rounded-lg text-[11px] sm:text-xs font-mono font-medium transition-all cursor-pointer min-h-[44px] flex items-center justify-center text-center whitespace-normal break-words leading-tight ${activeStep === 2 ? "bg-primary text-primary-foreground font-semibold shadow-[0_0_12px_var(--glow-primary)]" : "text-foreground/80 hover:bg-card hover:text-primary"}`}>
                  <span>2. Dims</span>
                </button>
                <button type="button" onClick={() => setActiveStep(3)} className={`w-full py-2.5 px-1.5 sm:px-3 rounded-lg text-[11px] sm:text-xs font-mono font-medium transition-all cursor-pointer min-h-[44px] flex items-center justify-center text-center whitespace-normal break-words leading-tight ${activeStep === 3 ? "bg-primary text-primary-foreground font-semibold shadow-[0_0_12px_var(--glow-primary)]" : "text-foreground/80 hover:bg-card hover:text-primary"}`}>
                  <span>3. Light</span>
                </button>
              </div>

              {/* Parameter Selector Card with glass/neon styling */}
              <Card className="ql-glass-strong text-card-foreground border-border shadow-xl relative overflow-hidden backdrop-blur-md min-w-0">
                <div className="absolute top-0 inset-x-0 h-0.5 bg-gradient-to-r from-primary via-accent-foreground to-transparent" />

                <CardHeader className="p-4 sm:p-6 pb-3 sm:pb-4 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 text-primary text-[11px] sm:text-xs font-semibold tracking-wider uppercase font-mono">
                      <Activity className="size-4 animate-pulse shrink-0" />
                      <span>Parameter Step 0{activeStep} / 03</span>
                    </div>
                    <span className="text-[11px] sm:text-xs text-muted-foreground font-mono">
                      ID: CONST-{activeStep * 104}
                    </span>
                  </div>
                  <CardTitle className="text-lg sm:text-2xl font-bold font-serif mt-2 text-card-foreground break-words">
                    {activeStep === 1 && "Select Gravitational Constant (G)"}
                    {activeStep === 2 && "Spatial Dimensions"}
                    {activeStep === 3 && "Speed of Light in Vacuum (c)"}
                  </CardTitle>
                  <CardDescription className="text-muted-foreground text-xs sm:text-sm font-sans break-words">
                    {activeStep === 1 && "Controls orbital cohesion, stellar collapse barriers, and structural mass limits."}
                    {activeStep === 2 && "Determines topological degrees of freedom, planar interactions, and orbital stability."}
                    {activeStep === 3 && "Regulates causal speed, cosmic radiation lag, and relativistic spacetime shear."}
                  </CardDescription>
                </CardHeader>

                <CardContent className="p-4 sm:p-6 pt-1 sm:pt-2 pb-5 sm:pb-6 min-w-0">
                  <AnimatePresence mode="wait">
                    {activeStep === 1 && <motion.div key="step1" initial={{
                    opacity: 0,
                    x: -10
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} exit={{
                    opacity: 0,
                    x: 10
                  }} transition={{
                    duration: 0.2
                  }} className="min-w-0">
                        <RadioGroup value={gravity} onValueChange={setGravity} className="gap-2.5 sm:gap-3">
                          <Label htmlFor="g-std" className={`flex items-start gap-3 sm:gap-4 p-3.5 sm:p-4 rounded-xl border cursor-pointer transition-all min-h-[48px] min-w-0 ${gravity === "standard" ? "border-primary bg-primary/15 shadow-[0_0_16px_var(--glow-primary)]" : "border-border bg-card/60 hover:border-primary/50 hover:bg-card"}`}>
                            <RadioGroupItem value="standard" id="g-std" className="mt-1 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <span className="font-semibold text-xs sm:text-base text-card-foreground flex items-center justify-between gap-1 flex-wrap">
                                <span className="break-words">Standard Reality (1.0x G)</span>
                                <span className="text-[11px] sm:text-xs text-primary font-mono shrink-0">6.674e-11</span>
                              </span>
                              <p className="text-[11px] sm:text-xs text-muted-foreground mt-1 font-normal font-sans break-words">
                                Present celestial metric allowing multi-billion year star life cycles.
                              </p>
                            </div>
                          </Label>

                          <Label htmlFor="g-hyper" className={`flex items-start gap-3 sm:gap-4 p-3.5 sm:p-4 rounded-xl border cursor-pointer transition-all min-h-[48px] min-w-0 ${gravity === "hyper" ? "border-primary bg-primary/15 shadow-[0_0_16px_var(--glow-primary)]" : "border-border bg-card/60 hover:border-primary/50 hover:bg-card"}`}>
                            <RadioGroupItem value="hyper" id="g-hyper" className="mt-1 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <span className="font-semibold text-xs sm:text-base text-card-foreground flex items-center justify-between gap-1 flex-wrap">
                                <span className="break-words">Hyper-Gravity (10.0x G)</span>
                                <span className="text-[11px] sm:text-xs text-destructive font-mono shrink-0">CRUSH TENSOR</span>
                              </span>
                              <p className="text-[11px] sm:text-xs text-muted-foreground mt-1 font-normal font-sans break-words">
                                Intense stellar collapse rate and catastrophic tidal compression.
                              </p>
                            </div>
                          </Label>

                          <Label htmlFor="g-micro" className={`flex items-start gap-3 sm:gap-4 p-3.5 sm:p-4 rounded-xl border cursor-pointer transition-all min-h-[48px] min-w-0 ${gravity === "micro" ? "border-primary bg-primary/15 shadow-[0_0_16px_var(--glow-primary)]" : "border-border bg-card/60 hover:border-primary/50 hover:bg-card"}`}>
                            <RadioGroupItem value="micro" id="g-micro" className="mt-1 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <span className="font-semibold text-xs sm:text-base text-card-foreground flex items-center justify-between gap-1 flex-wrap">
                                <span className="break-words">Micro-Gravity (0.1x G)</span>
                                <span className="text-[11px] sm:text-xs text-primary font-mono shrink-0">DIFFUSE TENSOR</span>
                              </span>
                              <p className="text-[11px] sm:text-xs text-muted-foreground mt-1 font-normal font-sans break-words">
                                Weak cosmic adhesion where nebular stellar ignitions fail to compress.
                              </p>
                            </div>
                          </Label>
                        </RadioGroup>
                      </motion.div>}

                    {activeStep === 2 && <motion.div key="step2" initial={{
                    opacity: 0,
                    x: -10
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} exit={{
                    opacity: 0,
                    x: 10
                  }} transition={{
                    duration: 0.2
                  }} className="min-w-0">
                        <RadioGroup value={dimensions} onValueChange={setDimensions} className="gap-2.5 sm:gap-3">
                          <Label htmlFor="d-3d" className={`flex items-start gap-3 sm:gap-4 p-3.5 sm:p-4 rounded-xl border cursor-pointer transition-all min-h-[48px] min-w-0 ${dimensions === "3d" ? "border-primary bg-primary/15 shadow-[0_0_16px_var(--glow-primary)]" : "border-border bg-card/60 hover:border-primary/50 hover:bg-card"}`}>
                            <RadioGroupItem value="3d" id="d-3d" className="mt-1 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <span className="font-semibold text-xs sm:text-base text-card-foreground flex items-center justify-between gap-1 flex-wrap">
                                <span className="break-words">3 Dimensions (Our Reality)</span>
                                <span className="text-[11px] sm:text-xs text-primary font-mono shrink-0">Euclidean 3D</span>
                              </span>
                              <p className="text-[11px] sm:text-xs text-muted-foreground mt-1 font-normal font-sans break-words">
                                Supports stable inverse-square orbits and complex volumetric biology.
                              </p>
                            </div>
                          </Label>

                          <Label htmlFor="d-2d" className={`flex items-start gap-3 sm:gap-4 p-3.5 sm:p-4 rounded-xl border cursor-pointer transition-all min-h-[48px] min-w-0 ${dimensions === "2d" ? "border-primary bg-primary/15 shadow-[0_0_16px_var(--glow-primary)]" : "border-border bg-card/60 hover:border-primary/50 hover:bg-card"}`}>
                            <RadioGroupItem value="2d" id="d-2d" className="mt-1 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <span className="font-semibold text-xs sm:text-base text-card-foreground flex items-center justify-between gap-1 flex-wrap">
                                <span className="break-words">2 Dimensions (Flatland)</span>
                                <span className="text-[11px] sm:text-xs text-destructive font-mono shrink-0">Planar</span>
                              </span>
                              <p className="text-[11px] sm:text-xs text-muted-foreground mt-1 font-normal font-sans break-words">
                                Zero cross-over pathways for internal digestive and neural structures.
                              </p>
                            </div>
                          </Label>

                          <Label htmlFor="d-4d" className={`flex items-start gap-3 sm:gap-4 p-3.5 sm:p-4 rounded-xl border cursor-pointer transition-all min-h-[48px] min-w-0 ${dimensions === "4d" ? "border-primary bg-primary/15 shadow-[0_0_16px_var(--glow-primary)]" : "border-border bg-card/60 hover:border-primary/50 hover:bg-card"}`}>
                            <RadioGroupItem value="4d" id="d-4d" className="mt-1 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <span className="font-semibold text-xs sm:text-base text-card-foreground flex items-center justify-between gap-1 flex-wrap">
                                <span className="break-words">4 Dimensions (Hyper-Space)</span>
                                <span className="text-[11px] sm:text-xs text-destructive font-mono shrink-0">4D Hyper</span>
                              </span>
                              <p className="text-[11px] sm:text-xs text-muted-foreground mt-1 font-normal font-sans break-words">
                                Inverse-cube force laws lead to rapid orbital instability and cosmic drift.
                              </p>
                            </div>
                          </Label>
                        </RadioGroup>
                      </motion.div>}

                    {activeStep === 3 && <motion.div key="step3" initial={{
                    opacity: 0,
                    x: -10
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} exit={{
                    opacity: 0,
                    x: 10
                  }} transition={{
                    duration: 0.2
                  }} className="min-w-0">
                        <RadioGroup value={speedOfLight} onValueChange={setSpeedOfLight} className="gap-2.5 sm:gap-3">
                          <Label htmlFor="c-std" className={`flex items-start gap-3 sm:gap-4 p-3.5 sm:p-4 rounded-xl border cursor-pointer transition-all min-h-[48px] min-w-0 ${speedOfLight === "standard" ? "border-primary bg-primary/15 shadow-[0_0_16px_var(--glow-primary)]" : "border-border bg-card/60 hover:border-primary/50 hover:bg-card"}`}>
                            <RadioGroupItem value="standard" id="c-std" className="mt-1 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <span className="font-semibold text-xs sm:text-base text-card-foreground flex items-center justify-between gap-1 flex-wrap">
                                <span className="break-words">Standard Velocity</span>
                                <span className="text-[11px] sm:text-xs text-primary font-mono shrink-0">299,792 km/s</span>
                              </span>
                              <p className="text-[11px] sm:text-xs text-muted-foreground mt-1 font-normal font-sans break-words">
                                Standard universal causality and atomic photon electromagnetic bindings.
                              </p>
                            </div>
                          </Label>

                          <Label htmlFor="c-sluggish" className={`flex items-start gap-3 sm:gap-4 p-3.5 sm:p-4 rounded-xl border cursor-pointer transition-all min-h-[48px] min-w-0 ${speedOfLight === "sluggish" ? "border-primary bg-primary/15 shadow-[0_0_16px_var(--glow-primary)]" : "border-border bg-card/60 hover:border-primary/50 hover:bg-card"}`}>
                            <RadioGroupItem value="sluggish" id="c-sluggish" className="mt-1 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <span className="font-semibold text-xs sm:text-base text-card-foreground flex items-center justify-between gap-1 flex-wrap">
                                <span className="break-words">Relativistic Sluggish</span>
                                <span className="text-[11px] sm:text-xs text-destructive font-mono shrink-0">100 km/h</span>
                              </span>
                              <p className="text-[11px] sm:text-xs text-muted-foreground mt-1 font-normal font-sans break-words">
                                Walking speeds generate blinding relativistic visual distortions.
                              </p>
                            </div>
                          </Label>

                          <Label htmlFor="c-infinite" className={`flex items-start gap-3 sm:gap-4 p-3.5 sm:p-4 rounded-xl border cursor-pointer transition-all min-h-[48px] min-w-0 ${speedOfLight === "infinite" ? "border-primary bg-primary/15 shadow-[0_0_16px_var(--glow-primary)]" : "border-border bg-card/60 hover:border-primary/50 hover:bg-card"}`}>
                            <RadioGroupItem value="infinite" id="c-infinite" className="mt-1 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <span className="font-semibold text-xs sm:text-base text-card-foreground flex items-center justify-between gap-1 flex-wrap">
                                <span className="break-words">Infinite Velocity</span>
                                <span className="text-[11px] sm:text-xs text-primary font-mono shrink-0">Instantaneous</span>
                              </span>
                              <p className="text-[11px] sm:text-xs text-muted-foreground mt-1 font-normal font-sans break-words">
                                Universal non-local communication with complete loss of relativistic horizon.
                              </p>
                            </div>
                          </Label>
                        </RadioGroup>
                      </motion.div>}
                  </AnimatePresence>
                </CardContent>

                {/* Step Navigation Controls */}
                <div className="px-4 sm:px-6 pb-4 sm:pb-6 pt-3 flex items-center justify-between border-t border-border gap-2 min-w-0">
                  <Button type="button" variant="outline" size="sm" disabled={activeStep === 1} onClick={() => setActiveStep(prev => Math.max(1, prev - 1))} className="text-xs ql-btn-ghost min-h-[44px] px-3.5 sm:px-4 cursor-pointer">
                    <ChevronLeft className="size-3.5 mr-1 shrink-0" />
                    <span>Previous</span>
                  </Button>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {[1, 2, 3].map(step => <span key={step} data-index={step - 1} className={`size-2 rounded-full transition-all ${activeStep === step ? "w-4 sm:w-5 bg-primary shadow-[0_0_6px_var(--glow-primary)]" : "bg-muted-foreground/30"}`} />)}
                  </div>
                  <Button type="button" variant="outline" size="sm" disabled={activeStep === 3} onClick={() => setActiveStep(prev => Math.min(3, prev + 1))} className="text-xs ql-btn min-h-[44px] px-3.5 sm:px-4 cursor-pointer">
                    <span>Next</span>
                    <ChevronRight className="size-3.5 ml-1 shrink-0" />
                  </Button>
                </div>
              </Card>

              {/* Active Parameters Overview Bar */}
              <div className="grid grid-cols-3 gap-2 sm:gap-3 p-3 sm:p-3.5 rounded-xl ql-glass border border-border text-center text-xs min-w-0">
                <div className="min-w-0">
                  <span className="text-muted-foreground block text-[10px] sm:text-[11px] uppercase font-mono truncate">Gravity</span>
                  <span className="font-mono font-bold text-primary text-xs sm:text-sm block truncate">
                    {gravity === "hyper" ? "10.0x G" : gravity === "micro" ? "0.1x G" : "1.0x G"}
                  </span>
                </div>
                <div className="min-w-0">
                  <span className="text-muted-foreground block text-[10px] sm:text-[11px] uppercase font-mono truncate">Dims</span>
                  <span className="font-mono font-bold text-primary text-xs sm:text-sm block truncate">
                    {dimensions === "2d" ? "2 Spatial" : dimensions === "4d" ? "4 Spatial" : "3 Spatial"}
                  </span>
                </div>
                <div className="min-w-0">
                  <span className="text-muted-foreground block text-[10px] sm:text-[11px] uppercase font-mono truncate">Light</span>
                  <span className="font-mono font-bold text-primary text-xs sm:text-sm block truncate">
                    {speedOfLight === "sluggish" ? "100 km/h" : speedOfLight === "infinite" ? "Infinite" : "300,000 km/s"}
                  </span>
                </div>
              </div>
            </div>

            {/* Columns 7-12: Dynamic Cosmic Outcome Terminal with Animated Result Card */}
            <div className="lg:col-span-6 min-w-0">
              <AnimatePresence mode="wait">
                <motion.div key={getOutcomeKey()} initial={{
                opacity: 0,
                scale: 0.98
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.98
              }} transition={{
                duration: 0.25
              }} className="min-w-0">
                  <Card className="ql-glass-strong text-card-foreground border-border shadow-2xl relative overflow-hidden min-w-0">
                    {/* Terminal Frame Header */}
                    <div className="bg-muted/80 px-4 sm:px-6 py-3 sm:py-3.5 border-b border-border flex items-center justify-between gap-2 min-w-0">
                      <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                        <span className="size-2 sm:size-2.5 rounded-full bg-destructive/80 shrink-0" />
                        <span className="size-2 sm:size-2.5 rounded-full bg-accent-foreground/60 shrink-0" />
                        <span className="size-2 sm:size-2.5 rounded-full bg-primary shrink-0" />
                        <span className="ml-1 sm:ml-2 font-mono text-[10px] sm:text-xs text-muted-foreground tracking-wide truncate">
                          COSMIC_TERMINAL // SIM_v4
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-[11px] sm:text-xs text-primary font-mono shrink-0">
                        <Zap className="size-3 sm:size-3.5 animate-pulse shrink-0" />
                        <span>LIVE COMPUTE</span>
                      </div>
                    </div>

                    <CardContent className="p-4 sm:p-8 space-y-5 sm:space-y-6 min-w-0">
                      {/* Projected State Header */}
                      <div className="space-y-2 min-w-0">
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 text-primary border border-primary/30 text-[11px] sm:text-xs font-mono">
                          <Compass className="size-3.5 shrink-0" />
                          <span>SYNTHESIS COMPLETE</span>
                        </div>
                        <h3 className="text-lg sm:text-2xl font-bold font-serif text-card-foreground tracking-tight leading-snug break-words">
                          {outcome.title}
                        </h3>
                        <p className="text-xs sm:text-sm font-mono text-primary/90 break-words">
                          Classification: {outcome.verdict}
                        </p>
                      </div>

                      {/* Analytical Telemetry Metrics */}
                      <div className="grid grid-cols-3 gap-2 sm:gap-3 p-3 sm:p-4 rounded-xl bg-muted/60 border border-border font-mono text-xs min-w-0">
                        <div className="min-w-0">
                          <span className="text-muted-foreground block text-[9px] sm:text-[10px] uppercase truncate">Stability</span>
                          <span className="text-card-foreground font-semibold flex items-center gap-1 mt-0.5 min-w-0">
                            <Orbit className="size-3 text-primary shrink-0" />
                            <span className="truncate text-xs sm:text-sm">{outcome.stability}</span>
                          </span>
                        </div>
                        <div className="min-w-0">
                          <span className="text-muted-foreground block text-[9px] sm:text-[10px] uppercase truncate">Entropy</span>
                          <span className="text-card-foreground font-semibold flex items-center gap-1 mt-0.5 min-w-0">
                            <Layers className="size-3 text-primary shrink-0" />
                            <span className="truncate text-xs sm:text-sm">{outcome.entropy}</span>
                          </span>
                        </div>
                        <div className="min-w-0">
                          <span className="text-muted-foreground block text-[9px] sm:text-[10px] uppercase truncate">Stellar Era</span>
                          <span className="text-card-foreground font-semibold flex items-center gap-1 mt-0.5 min-w-0">
                            <Cpu className="size-3 text-primary shrink-0" />
                            <span className="truncate text-xs sm:text-sm">{outcome.stellarLifespan}</span>
                          </span>
                        </div>
                      </div>

                      {/* Plain-Language Physical Ramifications */}
                      <div className="space-y-2 sm:space-y-3 min-w-0">
                        <h4 className="text-[11px] sm:text-xs uppercase font-mono tracking-wider text-muted-foreground">
                          Physical Ramifications & Consequences
                        </h4>
                        <div className="p-3.5 sm:p-4 rounded-xl bg-card/60 border border-border/80 min-w-0">
                          <p className="text-xs sm:text-base text-foreground/90 leading-relaxed font-sans break-words">
                            {outcome.description}
                          </p>
                        </div>
                      </div>

                      {/* Terminal Footnote & Recalibrate Action Button */}
                      <div className="pt-3 sm:pt-4 border-t border-border flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4 min-w-0">
                        <div className="flex items-center gap-2 text-[11px] sm:text-xs text-muted-foreground font-mono min-w-0">
                          <span className="size-1.5 rounded-full bg-primary shadow-[0_0_6px_var(--glow-primary)] shrink-0" />
                          <span className="truncate">Non-destructive testbed simulation</span>
                        </div>
                        <Button type="button" onClick={handleReset} className="ql-btn text-xs min-h-[44px] px-5 cursor-pointer w-full sm:w-auto">
                          <RefreshCw className="size-4 mr-1.5 text-primary-foreground shrink-0" />
                          <span>Recalibrate Parameters</span>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}